import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, Input, Output, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import multiMonthPlugin from '@fullcalendar/multimonth'
import interactionPlugin from '@fullcalendar/interaction';
import { CalendarModule, DateAdapter, CalendarView, CalendarDateFormatter } from 'angular-calendar';
import { FullCalendarModule } from '@fullcalendar/angular';
import { MatDialog } from '@angular/material/dialog';
import { EventEmitter } from '@angular/core';
import { OrderTripDate } from '../interfaces/trip.interface';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

@Component({
  selector: 'app-trip-datepicker',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatIconModule,
    MatMenuModule,
    MatNativeDateModule,
    OverlayModule,
    MatBadgeModule,
    FullCalendarModule
  ],
  templateUrl: './trip-datepicker.component.html',
  styleUrl: './trip-datepicker.component.scss'
})
export class TripDatepickerComponent {
  @Input() set initialDate(value: OrderTripDate) {
    this.updateDate(value);
  }
  @Input() showBothOptions: boolean = false;
  @Input() pastTotal: number = 0;
  @Input() futureTotal: number = 0;
  @Input() showFuture: boolean = true; // Nuevo Input Para Manejar Fechas Pasadas
  @Input() pastData: any = null; // Nuevo: datos de viajes pasados
  @Input() futureData: any = null;
  @Input() isLoading: boolean = false;
  @Output() dateChange = new EventEmitter<Date>();
  @Output() modeChanged = new EventEmitter<boolean>();
  @Output() menuOpened = new EventEmitter<void>();

  total = signal(0);
  isViewMenuOpen = signal(false);
  isOptionsMenuOpen: boolean = false;
  activeTrigger: CdkOverlayOrigin | null = null;

  view: CalendarView = CalendarView.Month;
  calendarOptions: any = {
    plugins: [multiMonthPlugin, interactionPlugin],
    initialView: 'multiMonthFourMonth',
    views: {
      multiMonthFourMonth: {
        type: 'multiMonth',
        duration: { months: 3 },
        buttonText: 'Year',
      },
    },
    events: [],
    headerToolbar: false,
    locale: 'es'
  };

  @ViewChild('popoverTrigger') popoverTrigger: any;
  currentEventData: any = {};

  constructor(
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void { }

  calendarVisibility(isVisible: boolean): void {
    this.isViewMenuOpen.set(isVisible);

  }

  private updateDate(value): void {
    this.total.set(value.total ?? 0);

    const events = value.days.map(dayInfo => {
      const hasTrips = dayInfo.total > 0;

      // Filtrar solo statuses con count > 0
      const filteredStatuses = dayInfo.statuses.filter(
        status => status.count.count > 0
      );

      return {
        title: `Total: ${dayInfo.total}`,
        start: dayInfo.day,
        allDay: true,
        backgroundColor: hasTrips ? '#4caf50' : '#f44336',
        borderColor: '#fff',
        textColor: '#fff',
        extendedProps: {
          statuses: filteredStatuses,  // Solo statuses con count > 0
          total: dayInfo.total,
        }
      };
    });
    // const events = value.days.map(dayInfo => (
    //   {
    //   title: `Total: ${dayInfo.total}`,
    //   start: dayInfo.day,
    //   allDay: true,
    //   backgroundColor: dayInfo.statuses.length > 0 ? '#4caf50' : '#f44336', // Verde si tiene trip, rojo si no
    //   borderColor: '#fff',
    //   textColor: '#fff',
    //   extendedProps: {  // Aquí guardas info extra
    //     withTrip: dayInfo.statuses,
    //     withoutTrip: dayInfo.withoutTrip,
    //     total: dayInfo.total
    //   }
    // }));

    let minDate: Date | undefined = undefined;
    if (value.days.length > 0) {
      minDate = new Date(
        Math.min(
          ...value.days.map((item) => new Date(item.day).getTime())
        )
      );
      minDate.setDate(1);
    } else {
      minDate = new Date();
      if (!this.showFuture) {
        minDate.setMonth(minDate.getMonth() - 2);
      }
      minDate.setDate(1);
    }

    const initialDate = new Date();
    if (!this.showFuture) {
      initialDate.setMonth(initialDate.getMonth() - 2);
    }

    this.calendarOptions = {
      ...this.calendarOptions,
      events,
      initialDate: initialDate,
      eventClick: (info: any) => {
        this.openPopover(info)
      },
      eventDidMount: (info: any) => {
        info.el.style.cursor = 'pointer';
      }
    };
  }

  switchMode(isFuture: boolean, trigger: CdkOverlayOrigin): void {
    this.showFuture = isFuture;
    this.activeTrigger = trigger;
    this.modeChanged.emit(isFuture);
    //this.isOptionsMenuOpen = false;

    // Actualizar los datos según el modo seleccionado
    if (isFuture && this.futureData) {
      this.updateDate(this.futureData); // Usar updateDate en lugar de asignar directamente
    } else if (!isFuture && this.pastData) {
      this.updateDate(this.pastData); // Usar updateDate en lugar de asignar directamente
    }
    this.calendarVisibility(true);
  }

  openPopover(info: any) {
    const event = info.event;
    const date = event.startStr;
    const { statuses, total } = event.extendedProps;
    this.currentEventData = { date, statuses, total };

    // Obtener la posición del elemento clicado en el calendario
    const targetElement = info.el.getBoundingClientRect();

    const buttonElement = this.popoverTrigger._element.nativeElement;
    buttonElement.style.top = `${targetElement.top + window.scrollY}px`;
    buttonElement.style.left = `${targetElement.left + window.scrollX}px`;
    buttonElement.style.width = `${targetElement.width}px`;
    buttonElement.style.height = `${targetElement.height}px`;

    setTimeout(() => this.popoverTrigger.openMenu(), 0);
  }

  search(currentEventData: any) {
    if (!currentEventData) return;

    this.popoverTrigger.closeMenu();
    if (this.isViewMenuOpen()) {
      this.calendarVisibility(false);
    }

    const date = new Date(currentEventData);
    const fixedDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

    this.dateChange.emit(fixedDate);
  }
}
